import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import { graphql } from '@/gql';
import {
  SyncEnergyPerformanceCertificateInput,
  SyncEnergyPerformanceCertificateMutation,
} from '@/gql/graphql';

const SYNC_ENERGY_PERFORMANCE_CERTIFICATE_MUTATION = graphql(`
  mutation SyncEnergyPerformanceCertificate(
    $input: SyncEnergyPerformanceCertificateInput!
  ) {
    syncEnergyPerformanceCertificate(input: $input) {
      __typename
      ... on SyncEnergyPerformanceCertificateFoundResponse {
        certificate {
          ...CertificateFields
        }
      }
      ... on SyncEnergyPerformanceCertificateUnchangedResponse {
        certificate {
          ...CertificateFields
        }
      }
      ... on SyncEnergyPerformanceCertificateErrorResponse {
        message
      }
      ... on SyncEnergyPerformanceCertificateConfirmationNeededResponse {
        certificates {
          ...CertificateFields
        }
      }
    }
  }

  fragment CertificateFields on EnergyPerformanceCertificate {
    metadata {
      certificateNumber
      lodgementDate
      lodgementDatetime
      inspectionDate
      reportType
    }
    property {
      address
      postcode
      uprn
      uprnSource
      localAuthority
      localAuthorityLabel
      constituency
      constituencyLabel
      propertyType
      builtForm
      constructionAgeBand
      numberHabitableRooms
      numberHeatedRooms
      numberOpenFireplaces
      flatTopStorey
      flatStoreyCount
      floorLevel
      extensionCount
      tenure
      totalFloorArea
      transactionType
    }
    energyPerformance {
      currentEnergyRating
      potentialEnergyRating
      currentEnergyEfficiency
      potentialEnergyEfficiency
      currentEnergyConsumption
      potentialEnergyConsumption
      currentCo2Emissions
      potentialCo2Emissions
      currentCo2EmissionsPerFloorArea
      currentEnvironmentImpact
      potentialEnvironmentImpact
    }
    buildingComponents {
      walls {
        description
        energyEfficiency
      }
      roof {
        description
        energyEfficiency
      }
      floor {
        description
        energyEfficiency
      }
      windows {
        description
        energyEfficiency
      }
      mainHeating {
        description
        energyEfficiency
      }
      mainHeatingControls {
        description
        energyEfficiency
      }
      secondaryHeating {
        description
        energyEfficiency
      }
      hotWater {
        description
        energyEfficiency
      }
      lighting {
        description
        energyEfficiency
      }
    }
    costs {
      currentHeatingCost
      potentialHeatingCost
      currentHotWaterCost
      potentialHotWaterCost
      currentLightingCost
      potentialLightingCost
    }
    technical {
      mainFuel
      mainHeatingControls
      floorHeight
      glazedType
      glazedArea
      multiGlazeProportion
      lowEnergyLighting
      lowEnergyFixedLightCount
      fixedLightingOutletsCount
      energyTariff
      mainsGasFlag
      solarWaterHeatingFlag
      mechanicalVentilation
      photoSupply
      windTurbineCount
      heatLossCorridor
      unheatedCorridorLength
    }
  }
`);

export type SyncEnergyPerformanceCertificateResponse =
  SyncEnergyPerformanceCertificateMutation['syncEnergyPerformanceCertificate'];

type SyncEnergyPerformanceCertificateFoundResponse = Extract<
  SyncEnergyPerformanceCertificateResponse,
  { __typename: 'SyncEnergyPerformanceCertificateFoundResponse' }
>;

type SyncEnergyPerformanceCertificateUnchangedResponse = Extract<
  SyncEnergyPerformanceCertificateResponse,
  { __typename: 'SyncEnergyPerformanceCertificateUnchangedResponse' }
>;

type SyncEnergyPerformanceCertificateErrorResponse = Extract<
  SyncEnergyPerformanceCertificateResponse,
  { __typename: 'SyncEnergyPerformanceCertificateErrorResponse' }
>;

export type SyncEnergyPerformanceCertificateConfirmationNeededResponse =
  Extract<
    SyncEnergyPerformanceCertificateResponse,
    {
      __typename: 'SyncEnergyPerformanceCertificateConfirmationNeededResponse';
    }
  >;

const useSyncEnergyPerformanceCertificate = (args?: {
  projectId?: string;
  options?: UseMutationOptions<
    SyncEnergyPerformanceCertificateMutation,
    unknown,
    SyncEnergyPerformanceCertificateInput
  >;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input) => {
      return gqlClient.request(SYNC_ENERGY_PERFORMANCE_CERTIFICATE_MUTATION, {
        input,
      });
    },
    onSuccess: () => {
      if (args?.projectId) {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.overview(args.projectId),
        });
        void queryClient.invalidateQueries({
          queryKey: projectKeys.heatLossEstimate(args.projectId),
        });
      }
    },
    ...args?.options,
  });
};

export default useSyncEnergyPerformanceCertificate;
